import { useMemo, useState } from "react";
import { useStyles } from "./AdoTab.styles";
import { SummarySection } from "./sections/SummarySection/SummarySection";
import { VelocitySection } from "./sections/VelocitySection/VelocitySection";
import { QualitySection } from "./sections/QualitySection/QualitySection";
import { OrganizationQualitySection } from "./sections/OrganizationQualitySection/OrganizationQualitySection";
import { DeliverySection } from "./sections/DeliverySection/DeliverySection";
import { PrHealthSection } from "./sections/PrHealthSection/PrHealthSection";
import { useAiMetrics } from "../../context/AiMetricsContext";
import { useAdoDashboard } from "../../hooks/useAdoDashboard";
import { useAdoThroughputByTeam } from "../../hooks/useAdoThroughputByTeam";
import { useAdoCycleTimeTrend } from "../../hooks/useAdoCycleTimeTrend";
import { useAdoBugSeveritySplit } from "../../hooks/useAdoBugSeveritySplit";
import { useAdoTestCasesByTeam } from "../../hooks/useAdoTestCasesByTeam";
import { useAdoFeatureDeliveryByTeam } from "../../hooks/useAdoFeatureDeliveryByTeam";
import { useTeamDailyCost } from "../../hooks/useTeamDailyCost";
import { useTeamVelocity } from "../../hooks/useTeamVelocity";
import { useSprintDeliveryCost } from "../../hooks/useSprintDeliveryCost";
import { useAdoTeams } from "../../hooks/useAdoTeams";
import { useAdoSprints } from "../../hooks/useAdoSprints";
import { FiltersBar, IEntityFilterConfig } from "../FiltersBar/FiltersBar";
import {
  adaptVelocity,
  adaptQuality,
  adaptDelivery,
  adaptSprintChart,
  adaptBySprint,
  adaptByTeamComparison,
  adaptBugSeveritySplit,
} from "../../utils/dashboardAdapter";

// Stored sprint names keep the iteration path below the project root, so a real
// sprint always reads `Q4 2026\Sprint 26`. A name without the separator is a bare
// container node (a quarter or the root itself) that no work item sits under.
const SPRINT_PATH_SEPARATOR = "\\";

export const AdoTab = (): JSX.Element => {
  const styles = useStyles();
  const {
    selectedTeams,
    setSelectedTeams,
    selectedSprints,
    setSelectedSprints,
    startDate,
    endDate,
    currentPeriod,
    priorPeriod,
    comparisonEnabled,
    comparisonLabel,
    hasPersistedFilters,
  } = useAiMetrics();

  const {
    data: adoTeams,
    isLoading: adoTeamsLoading,
    isError: adoTeamsError,
  } = useAdoTeams();

  const {
    data: adoSprints,
    isLoading: adoSprintsLoading,
    isError: adoSprintsError,
  } = useAdoSprints();

  const adoEntityConfig: IEntityFilterConfig = useMemo(
    () => ({
      label: "Team",
      options: (adoTeams ?? []).map((t) => ({
        id: t.team_id,
        name: t.team_name,
      })),
      isLoading: adoTeamsLoading,
      isError: adoTeamsError,
      selected: selectedTeams,
      onChange: setSelectedTeams,
      loadingText: "Loading teams...",
      allLabel: "All Teams",
      skipAutoSelectAll: hasPersistedFilters,
    }),
    [
      adoTeams,
      adoTeamsLoading,
      adoTeamsError,
      selectedTeams,
      setSelectedTeams,
      hasPersistedFilters,
    ],
  );

  const sprintEntityConfig: IEntityFilterConfig = useMemo(
    () => ({
      label: "Sprint",
      options: (adoSprints ?? [])
        .filter(
          (sprint) =>
            sprint.sprint.includes(SPRINT_PATH_SEPARATOR) &&
            !sprint.sprint.includes("360PI\\"),
        )
        .map((sprint) => ({
          id: sprint.sprint,
          name: sprint.sprint,
          endDate: sprint.sprint_end ? new Date(sprint.sprint_end) : null,
        })),
      isLoading: adoSprintsLoading,
      isError: adoSprintsError,
      selected: selectedSprints,
      onChange: setSelectedSprints,
      loadingText: "Loading sprints...",
      allLabel: "All Sprints",
      skipAutoSelectAll: true,
      preserveOptionOrder: true,
      // The metric endpoints intersect the sprint and date filters, so a sprint
      // ending outside the window can only ever return an empty dashboard.
      // `currentPeriod` rather than `startDate`/`endDate` because that is the
      // window actually sent to the API - the two differ in Rolling 3-Month mode.
      dateRange: currentPeriod,
    }),
    [
      adoSprints,
      adoSprintsLoading,
      adoSprintsError,
      selectedSprints,
      setSelectedSprints,
      currentPeriod,
    ],
  );

  const args = [
    selectedTeams,
    currentPeriod.start,
    currentPeriod.end,
    priorPeriod.start,
    priorPeriod.end,
    comparisonEnabled,
  ] as const;

  const monthRangeArgs = [
    selectedTeams,
    startDate,
    endDate,
    startDate,
    endDate,
    false,
  ] as const;

  const dashboard = useAdoDashboard(...args, selectedSprints);
  const throughputByTeam = useAdoThroughputByTeam(...args, selectedSprints);
  const cycleTimeTrend = useAdoCycleTimeTrend(...args, selectedSprints);
  const organizationDashboard = useAdoDashboard(
    [],
    currentPeriod.start,
    currentPeriod.end,
    priorPeriod.start,
    priorPeriod.end,
    comparisonEnabled,
    [],
    { isProjectWideQuality: true },
  );
  const organizationBugSeveritySplit = useAdoBugSeveritySplit(
    [],
    startDate,
    endDate,
    startDate,
    endDate,
    false,
    [],
    { isProjectWideQuality: true },
  );
  const testCasesByTeam = useAdoTestCasesByTeam(
    ...monthRangeArgs,
    selectedSprints,
  );
  const featureDeliveryByTeam = useAdoFeatureDeliveryByTeam(
    ...monthRangeArgs,
    selectedSprints,
  );
  console.log("HEREEEE:");
  console.log(featureDeliveryByTeam);
  const teamDailyCost = useTeamDailyCost(...args);
  const teamVelocity = useTeamVelocity(...args, selectedSprints);
  // The chart's own toggle, kept here because the split has to be asked of the
  // API - the collapsed response cannot be broken back apart on the client.
  const [isSprintDeliverySplitByTeam, setIsSprintDeliverySplitByTeam] =
    useState(false);
  const sprintDeliveryCost = useSprintDeliveryCost(
    selectedTeams,
    currentPeriod.start,
    currentPeriod.end,
    selectedSprints,
    isSprintDeliverySplitByTeam,
  );

  const [currentLabel = "", priorLabel = ""] = comparisonEnabled
    ? comparisonLabel.split(" vs ")
    : [comparisonLabel, ""];

  const velocity = useMemo(
    () =>
      dashboard.current.data
        ? adaptVelocity(dashboard.current.data, comparisonEnabled)
        : undefined,
    [dashboard.current.data, comparisonEnabled],
  );
  const quality = useMemo(
    () =>
      dashboard.current.data
        ? adaptQuality(dashboard.current.data, comparisonEnabled)
        : undefined,
    [dashboard.current.data, comparisonEnabled],
  );
  const delivery = useMemo(
    () =>
      dashboard.current.data
        ? adaptDelivery(dashboard.current.data, comparisonEnabled)
        : undefined,
    [dashboard.current.data, comparisonEnabled],
  );
  const organizationQuality = useMemo(
    () =>
      organizationDashboard.current.data
        ? adaptQuality(organizationDashboard.current.data, false)
        : undefined,
    [organizationDashboard.current.data],
  );

  const throughputSprintChart = useMemo(
    () =>
      !comparisonEnabled && throughputByTeam.current.data?.length
        ? adaptBySprint(throughputByTeam.current.data)
        : undefined,
    [comparisonEnabled, throughputByTeam.current.data],
  );

  const throughputComparisonChart = useMemo(
    () =>
      comparisonEnabled && throughputByTeam.current.data?.length
        ? adaptByTeamComparison(
            throughputByTeam.current.data,
            throughputByTeam.prior.data ?? [],
            currentLabel,
            priorLabel,
          )
        : undefined,
    [
      comparisonEnabled,
      throughputByTeam.current.data,
      throughputByTeam.prior.data,
      currentLabel,
      priorLabel,
    ],
  );

  const cycleTimeSprintChart = useMemo(
    () =>
      !comparisonEnabled && cycleTimeTrend.current.data?.length
        ? adaptBySprint(cycleTimeTrend.current.data)
        : undefined,
    [comparisonEnabled, cycleTimeTrend.current.data],
  );

  const cycleTimeComparisonChart = useMemo(
    () =>
      comparisonEnabled && cycleTimeTrend.current.data?.length
        ? adaptByTeamComparison(
            cycleTimeTrend.current.data,
            cycleTimeTrend.prior.data ?? [],
            currentLabel,
            priorLabel,
            "avg",
          )
        : undefined,
    [
      comparisonEnabled,
      cycleTimeTrend.current.data,
      cycleTimeTrend.prior.data,
      currentLabel,
      priorLabel,
    ],
  );

  const organizationBugSeverityChart = useMemo(
    () =>
      organizationBugSeveritySplit.current.data?.length
        ? adaptBugSeveritySplit(organizationBugSeveritySplit.current.data)
        : undefined,
    [organizationBugSeveritySplit.current.data],
  );

  const testCasesChart = useMemo(
    () =>
      testCasesByTeam.current.data?.length
        ? adaptSprintChart(testCasesByTeam.current.data, [], "", "")
        : undefined,
    [testCasesByTeam.current.data],
  );

  const featureDeliveryChart = useMemo(
    () =>
      featureDeliveryByTeam.current.data?.length
        ? adaptSprintChart(featureDeliveryByTeam.current.data, [], "", "")
        : undefined,
    [featureDeliveryByTeam.current.data],
  );

  return (
    <>
      <FiltersBar entity={adoEntityConfig} sprint={sprintEntityConfig} />
      <div className={styles.contentPadding}>
        {selectedTeams.length === 0 ? (
          <div className={styles.tab}>
            <div className={styles.emptyState} role="status" aria-live="polite">
              <span className={styles.emptyStateTitle}>No team selected</span>
              <span>
                Select one or more teams from the dropdown to view team-level
                metrics.
              </span>
            </div>
            <OrganizationQualitySection
              kpis={organizationQuality}
              loading={organizationDashboard.current.isLoading}
              bugSeverity={organizationBugSeverityChart}
              bugSeverityLoading={
                organizationBugSeveritySplit.current.isLoading
              }
              bugSeverityError={organizationBugSeveritySplit.current.isError}
            />
          </div>
        ) : (
          <div className={styles.tab}>
            <SummarySection
              velocityKpis={velocity}
              loading={dashboard.current.isLoading}
              comparisonEnabled={comparisonEnabled}
              velocity={teamVelocity.current.data?.velocity}
              velocityPrior={teamVelocity.prior.data?.velocity}
              velocityLoading={
                teamVelocity.current.isLoading ||
                (comparisonEnabled && teamVelocity.prior.isLoading)
              }
              velocityError={teamVelocity.current.isError}
              spendCost={teamDailyCost.current.data?.cost}
              spendTokens={teamDailyCost.current.data?.tokens}
              spendPriorCost={teamDailyCost.prior.data?.cost}
              spendPriorTokens={teamDailyCost.prior.data?.tokens}
              spendLoading={
                teamDailyCost.current.isLoading ||
                (comparisonEnabled && teamDailyCost.prior.isLoading)
              }
              spendError={teamDailyCost.current.isError}
              sprintDelivery={sprintDeliveryCost.data}
              sprintDeliveryLoading={sprintDeliveryCost.isLoading}
              sprintDeliveryError={sprintDeliveryCost.isError}
              isSprintDeliverySplitByTeam={isSprintDeliverySplitByTeam}
              onSprintDeliverySplitByTeamChange={setIsSprintDeliverySplitByTeam}
            />
            <VelocitySection
              kpis={velocity}
              loading={dashboard.current.isLoading}
              error={dashboard.current.isError}
              throughputLoading={throughputByTeam.current.isLoading}
              comparisonEnabled={comparisonEnabled}
              teamCount={selectedTeams.length}
              velocity={teamVelocity.current.data?.velocity}
              velocityPrior={teamVelocity.prior.data?.velocity}
              velocityLoading={
                teamVelocity.current.isLoading ||
                (comparisonEnabled && teamVelocity.prior.isLoading)
              }
              velocityError={teamVelocity.current.isError}
              throughputSprintData={throughputSprintChart}
              throughputComparisonData={throughputComparisonChart}
              cycleTimeSprintData={cycleTimeSprintChart}
              cycleTimeComparisonData={cycleTimeComparisonChart}
              cycleTimeLoading={cycleTimeTrend.current.isLoading}
              cycleTimeError={cycleTimeTrend.current.isError}
            />
            <QualitySection
              kpis={quality}
              loading={dashboard.current.isLoading}
              testCasesByTeam={testCasesChart}
              testCasesLoading={testCasesByTeam.current.isLoading}
              testCasesError={testCasesByTeam.current.isError}
              comparisonEnabled={false}
              teamCount={selectedTeams.length}
            />
            <OrganizationQualitySection
              kpis={organizationQuality}
              loading={organizationDashboard.current.isLoading}
              bugSeverity={organizationBugSeverityChart}
              bugSeverityLoading={
                organizationBugSeveritySplit.current.isLoading
              }
              bugSeverityError={organizationBugSeveritySplit.current.isError}
            />
            <DeliverySection
              kpis={delivery}
              loading={dashboard.current.isLoading}
              error={dashboard.current.isError}
              featureDeliveryByTeam={featureDeliveryChart}
              featureDeliveryLoading={featureDeliveryByTeam.current.isLoading}
              comparisonEnabled={false}
              teamCount={selectedTeams.length}
            />
            <PrHealthSection />
          </div>
        )}
      </div>
    </>
  );
};
